import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CartItem from './components/CartItem';
import BundleSuggestions from './components/BundleSuggestions';
import RecommendedItems from './components/RecommendedItems';
import OrderSummary from './components/OrderSummary';
import CompatibilityWarnings from './components/CompatibilityWarnings';
import EmptyCart from './components/EmptyCart';
import FeaturedModels from './components/FeaturedModels';
import { cartService } from '../../services/cartService';
import { couponService } from '../../services/couponService';
import { bundleService } from '../../services/bundleService';
import { userGarageService } from '../../services/userGarageService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function ShoppingCart() {
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useAuth();
  const user = authContext?.user || null;
  const authLoading = authContext?.loading ?? true;
  const initialCheckComplete = authContext?.initialCheckComplete ?? false;
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateQuantityLoading, setUpdateQuantityLoading] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [showCompatibilityWarnings, setShowCompatibilityWarnings] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [refreshFavorites, setRefreshFavorites] = useState(0);
  const [estimatedTax, setEstimatedTax] = useState(0);
  const [userAddress, setUserAddress] = useState(null);

  const [directUser, setDirectUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  const effectiveUser = user || directUser;

  // Fetch default shipping address
  useEffect(() => {
    const fetchAddress = async () => {
      if (effectiveUser?.id) {
        try {
          const { data, error } = await supabase
            .from('user_addresses')
            .select('*')
            .eq('user_id', effectiveUser.id)
            .eq('is_default', true)
            .eq('address_type', 'shipping')
            .maybeSingle(); // Use maybeSingle to avoid error if none found
          
          if (!error && data) {
            setUserAddress(data);
          } else {
             // Fallback: Try to find ANY shipping address
             const { data: anyData } = await supabase
              .from('user_addresses')
              .select('*')
              .eq('user_id', effectiveUser.id)
              .eq('address_type', 'shipping')
              .limit(1)
              .maybeSingle();
             if (anyData) setUserAddress(anyData);
          }
        } catch (err) {
          console.error('Error fetching address for tax:', err);
        }
      }
    };
    fetchAddress();
  }, [effectiveUser?.id]);

  useEffect(() => {
    if (user) {
      setDirectUser(null);
      setSessionChecked(true);
      return;
    }

    if (!sessionChecked) {
      const checkSession = async () => {
        try {
          const { data: { session } } = await supabase?.auth?.getSession();
          if (session?.user) {
            setDirectUser(session?.user);
          } else {
            setDirectUser(null);
          }
        } catch (err) {
          console.error('ShoppingCart - Direct session check failed:', err);
          setDirectUser(null);
        } finally {
          setSessionChecked(true);
        }
      };

      checkSession();
    }
  }, [user, sessionChecked]);

  // Fetch cart items when component mounts or user changes
  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const authTimeout = setTimeout(() => {
      if ((authLoading || !initialCheckComplete) && !effectiveUser) {
        console.warn('ShoppingCart - Auth initialization timeout, stopping loading');
        setLoading(false);
        setError('Unable to verify authentication. Please refresh the page.');
      }
    }, 5000); // 5 second timeout for auth check

    const fetchCartItems = async () => {
      // Wait for auth to finish initializing (with timeout protection)
      if ((authLoading || !initialCheckComplete) && !effectiveUser) {
        return;
      }

      clearTimeout(authTimeout);

      // If no user, stop loading immediately and show empty state
      if (!effectiveUser?.id) {
        console.log('ShoppingCart - No authenticated user, showing empty cart message');
        setCartItems([]);
        setLoading(false);
        setError(null); // Clear any errors
        return;
      }

      // Fetch cart items for authenticated user (with timeout)
      try {
        setLoading(true);
        setError(null); // Clear any previous errors

        // Add timeout to cart fetch
        const cartFetchPromise = cartService?.getCartItems(effectiveUser?.id);
        const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Cart loading timed out')), 8000)
        );

        const items = await Promise.race([cartFetchPromise, timeoutPromise]);

        // Ensure we always set an array, even if empty
        const validItems = Array.isArray(items) ? items : [];
        
        let autoCorrectedCount = 0;
        
        // Automatically uncheck any items that are out of stock and enforce limits
        const itemsWithStockCheck = await Promise.all(validItems.map(async (item) => {
          let maxAllowed = item.stockQuantity;
          if (item.purchaseLimit !== null && item.purchaseLimit !== undefined) {
             maxAllowed = Math.min(maxAllowed ?? Infinity, item.purchaseLimit);
          }
          
          if (maxAllowed !== null && maxAllowed !== undefined && item.quantity > maxAllowed && maxAllowed > 0) {
             try {
                await cartService?.updateQuantity(item.cartItemId || item.id, maxAllowed);
                item.quantity = maxAllowed;
                autoCorrectedCount++;
             } catch(e) {
                console.error('Failed to auto-correct quantity:', e);
             }
          }
          
          return {
            ...item,
            selected: item.stockQuantity > 0 ? item.selected : false
          };
        }));
        
        if (autoCorrectedCount > 0) {
           setError(`Some item quantities in your cart were automatically reduced because they exceeded the available stock or purchase limits.`);
        }
        
        setCartItems(itemsWithStockCheck);

        // FIXED: Reset coupon and discount when cart is reloaded
        // This ensures the order summary refreshes with correct values after checkout
        setAppliedCoupon(null);
        setDiscount(0);

      } catch (err) {
        console.error('ShoppingCart - Error fetching cart:', err);
        if (err?.message?.includes('timed out')) {
          setError('Loading cart items is taking longer than expected. Please refresh the page.');
        } else {
          setError('Failed to load cart items. Please refresh the page.');
        }
        setCartItems([]); // Set empty array on error

        // Also reset coupon on error
        setAppliedCoupon(null);
        setDiscount(0);

      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();

    return () => {
      clearTimeout(authTimeout);
    };
  }, [effectiveUser?.id, authLoading, initialCheckComplete, effectiveUser, location?.pathname, location?.search, location?.key]);

  useEffect(() => {
    const selectedItems = cartItems?.filter((item) => item?.selected);
    setSelectAll(selectedItems?.length === cartItems?.length && cartItems?.length > 0);
  }, [cartItems]);

  // FIXED: Initialize selection state when cart items are loaded
  useEffect(() => {
    if (cartItems?.length > 0 && !cartItems?.some((item) => item?.selected)) {
      // Auto-select all IN-STOCK items on initial load for better UX
      setCartItems((items) => items?.map((item) => ({ 
        ...item, 
        selected: item.stockQuantity > 0 
      })));
    }
  }, [cartItems?.length]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setCartItems((items) => items?.map((item) => ({ 
      ...item, 
      selected: item.stockQuantity > 0 ? newSelectAll : false 
    })));
  };

  const handleToggleSelect = (itemId) => {
    setCartItems((items) =>
      items?.map((item) => {
        if ((item?.cartItemId || item?.id) === itemId) {
          // Only allow selecting if item is in stock
          if (item.stockQuantity <= 0 && !item.selected) return item;
          return { ...item, selected: !item?.selected };
        }
        return item;
      })
    );
  };

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      setError(null);
      const itemToUpdate = cartItems.find(i => i.cartItemId === itemId || i.id === itemId);
      const result = await cartService?.updateQuantity(itemId, newQuantity);
      const finalQuantity = result?.quantity || newQuantity;
      
      setCartItems((items) =>
        items?.map((item) =>
          item?.cartItemId === itemId || (itemToUpdate?.bundleId && item?.bundleId === itemToUpdate.bundleId)
            ? { ...item, quantity: finalQuantity }
            : item
        )
      );
      
      if (finalQuantity !== newQuantity) {
         setError(`Quantity was adjusted because you reached the maximum available stock or purchase limit.`);
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
      setError(err?.message || 'Failed to update quantity');
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      // Find the item to check for bundleId
      const itemToRemove = cartItems.find(i => i.cartItemId === itemId || i.id === itemId);
      
      if (itemToRemove?.bundleId) {
          // Find all items with this bundleId
          const bundleItems = cartItems.filter(i => i.bundleId === itemToRemove.bundleId);
          // Remove all
          await Promise.all(bundleItems.map(i => cartService.removeFromCart(i.cartItemId || i.id)));
          setCartItems(items => items.filter(i => i.bundleId !== itemToRemove.bundleId));
      } else {
          // Standard remove
          await cartService?.removeFromCart(itemId);
          setCartItems((items) => items?.filter((item) => item?.cartItemId !== itemId));
      }
    } catch (err) {
      console.error('Error removing item:', err);
      setError(err?.message || 'Failed to remove item');
    }
  };

  const handleMoveToWishlist = async (itemId) => {
    if (!effectiveUser?.id) {
      setError('You must be logged in to save favorites');
      return;
    }

    try {
      // Find the cart item to get the product ID
      const cartItem = cartItems?.find((item) => item?.cartItemId === itemId);

      if (!cartItem) {
        console.error('Cart item not found:', itemId);
        return;
      }

      console.log('Moving to saved for later - Cart Item:', cartItem);
      console.log('Product ID to save:', cartItem?.productId);
      console.log('Component ID to save:', cartItem?.componentId);

      const itemToSaveId = cartItem?.isComponent ? cartItem?.componentId : cartItem?.productId;
      const itemToSaveType = cartItem?.isComponent ? 'component' : 'product';

      if (!itemToSaveId) {
        console.error('No valid ID found for item to save:', cartItem);
        setError('Could not identify item to save for later. Please try again.');
        return;
      }

      // Add to user_garage with correct source
      await userGarageService?.addToGarage(
        itemToSaveId, 
        '', // no notes
        'saved_for_later', // source: saved from shopping cart
        itemToSaveType
      );
      console.log('Successfully added to saved for later');

      // Then remove from cart
      await handleRemoveItem(itemId);

      // Trigger FeaturedModels refresh
      setRefreshFavorites((prev) => prev + 1);

      console.log('Item moved to saved for later successfully');
    } catch (err) {
      console.error('Error moving item to saved for later:', err);

      // If the item is already saved, still remove it from cart
      if (err?.message?.includes('already in your collection')) {
        await handleRemoveItem(itemId);
        setRefreshFavorites((prev) => prev + 1);
      } else {
        setError(err?.message || 'Failed to save item for later');
      }
    }
  };

  // State for Bundles
  const [bundles, setBundles] = useState([]);

  // Fetch Bundles on Load
  useEffect(() => {
    const fetchBundles = async () => {
      const data = await bundleService.getActiveBundles();
      setBundles(data || []);
    };
    fetchBundles();
  }, []);

  const mockCompatibilityWarnings = [
  {
    id: 1,
    severity: "warning",
    title: "Scale Mismatch Detected",
    description: "Some items may appear disproportionate when used together due to different scaling standards.",
    affectedItems: cartItems?.slice(0, 2)?.map((item) => item?.name),
    suggestions: [
    "Adjust scale to match proportions",
    "Use the auto-scale feature in the 3D viewer",
    "Consider alternative models with standard scaling"],
    canResolve: true,
    hasAlternatives: true
  }];

  // Handle adding bundle to cart
  const handleAddBundle = async (bundleId) => {
    if (!effectiveUser?.id) {
      setError('Please sign in to add bundles to your cart.');
      return;
    }
    try {
      setLoading(true);
      await bundleService.addBundleToCart(effectiveUser.id, bundleId);
      
      // Refresh cart items
      const items = await cartService.getCartItems(effectiveUser.id);
      
      // Ensure only the newly added bundle items are selected
      const itemsWithSelection = Array.isArray(items) ? items.map(item => ({
        ...item,
        selected: item.bundleId === bundleId && item.stockQuantity > 0
      })) : [];

      setCartItems(itemsWithSelection);
      
      // Reset coupon/discount as cart changed
      setAppliedCoupon(null);
      setDiscount(0);
    } catch (err) {
      console.error('Error adding bundle:', err);
      setError('Failed to add bundle to cart.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = (itemId) => {
    console.log('Adding suggested item to cart:', itemId);
  };

  const handleApplyCoupon = async (couponCode) => {
    try {
      const result = await couponService.validateCoupon(couponCode?.toUpperCase());
      
      if (!result.valid) {
        setNotification({ show: true, message: result.message, type: 'error' });
        return;
      }

      const coupon = result.coupon;
      const selectedItems = cartItems?.filter((item) => item?.selected) || [];
      const subtotal = selectedItems?.reduce((sum, item) => {
          const priceToUse = item.bundleId ? item.cartPrice : item.price;
          return sum + (priceToUse || 0) * item?.quantity;
      }, 0);

      if (subtotal < coupon.minPurchaseAmount) {
        setNotification({ 
          show: true, 
          message: `Minimum purchase amount of ${formatPrice(coupon.minPurchaseAmount)} required for this coupon.`, 
          type: 'error' 
        });
        return;
      }

      // Calculate discountable subtotal based on "appliesTo"
      let discountableSubtotal = 0;
      if (coupon.appliesTo === 'all') {
        discountableSubtotal = subtotal;
      } else if (coupon.appliesTo === 'specific_products') {
        discountableSubtotal = selectedItems
          .filter(item => item.type === 'product' && coupon.applicableItemIds.includes(item.productId))
          .reduce((sum, item) => sum + (item.bundleId ? item.cartPrice : item.price) * item.quantity, 0);
      } else if (coupon.appliesTo === 'specific_components') {
        discountableSubtotal = selectedItems
          .filter(item => item.type === 'component' && coupon.applicableItemIds.includes(item.componentId))
          .reduce((sum, item) => sum + (item.bundleId ? item.cartPrice : item.price) * item.quantity, 0);
      }

      if (discountableSubtotal === 0) {
        setNotification({ 
          show: true, 
          message: 'This coupon does not apply to the items in your cart.', 
          type: 'error' 
        });
        return;
      }

      let calculatedDiscount = 0;
      if (coupon.discountType === 'percentage') {
        calculatedDiscount = discountableSubtotal * (coupon.discountValue / 100);
        if (coupon.maxDiscountAmount && calculatedDiscount > coupon.maxDiscountAmount) {
          calculatedDiscount = coupon.maxDiscountAmount;
        }
      } else {
        calculatedDiscount = coupon.discountValue;
      }

      // Cap discount at total subtotal
      calculatedDiscount = Math.min(calculatedDiscount, subtotal);

      setAppliedCoupon(coupon.code);
      setDiscount(calculatedDiscount);
      setNotification({ show: true, message: 'Coupon applied successfully!', type: 'success' });

    } catch (err) {
      console.error('Error applying coupon:', err);
      setNotification({ show: true, message: 'Failed to apply coupon.', type: 'error' });
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
  };

  const handleProceedToCheckout = () => {
    // Only proceed with items that are selected AND in stock
    const validItemsToCheckout = cartItems?.filter((item) => item?.selected && item?.stockQuantity > 0);
    
    if (validItemsToCheckout.length === 0) {
      const hasOutOfStockSelection = cartItems?.some(item => item?.selected && item?.stockQuantity <= 0);
      if (hasOutOfStockSelection) {
         setError(`All selected items are currently out of stock. Please select available items to proceed.`);
      } else {
         setError(`Please select at least one item to proceed.`);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const selectedItemIds = validItemsToCheckout.map((item) => item?.cartItemId || item?.id);

    // Redirect to Checkout Portal (proxied)
    const params = new URLSearchParams();
    if (selectedItemIds.length > 0) {
      params.append('selectedCartItemIds', selectedItemIds.join(','));
    }
    
    // Use navigate for internal routing to the wrapper
    navigate(`/checkout/?${params.toString()}`);
  };

  const handleResolveWarning = (warningId) => {
    console.log('Resolving compatibility warning:', warningId);
  };

  const handleIgnoreWarning = (warningId) => {
    console.log('Ignoring compatibility warning:', warningId);
  };

  const handleAddFeaturedToCart = async (modelId, isComponent) => {
    if (!effectiveUser?.id) {
      console.warn('User must be logged in to add to cart');
      return;
    }

    try {
      // Add product or component to cart with correct object format
      await cartService?.addToCart({
        userId: effectiveUser?.id,
        productId: isComponent ? null : modelId,
        componentId: isComponent ? modelId : null,
        quantity: 1
      });

      // Refresh cart items
      const items = await cartService?.getCartItems(effectiveUser?.id);
      setCartItems(Array.isArray(items) ? items : []);

      console.log('Item added to cart successfully');
    } catch (err) {
      console.error('Error adding item to cart:', err);
      const errMsg = err?.message || '';
      if (errMsg.includes('not currently available') || errMsg.includes('active seller') || errMsg.includes('p_seller_id')) {
        setError('This item is currently unavailable or out of stock from active sellers.');
      } else {
        setError('Failed to add item to cart. Please try again later.');
      }
    }
  };

  // Remove mock featured models - now fetched in FeaturedModels component
  // const mockFeaturedModels = [...];

  // Calculate totals - ONLY include in-stock selected items
  const validSelectedItems = cartItems?.filter((item) => item?.selected && item?.stockQuantity > 0);
  
  const itemCount = validSelectedItems?.reduce((count, item) => count + (item?.quantity || 0), 0);
  
  // Gross Subtotal (using Primary Displayed Price logic)
  const subtotal = validSelectedItems?.reduce((sum, item) => {
      // For bundles, Primary is Discounted Price (cartPrice)
      // For regular, Primary is Current Price (price)
      const priceToUse = item.bundleId ? item.cartPrice : item.price;
      return sum + (priceToUse || 0) * (item?.quantity || 0);
  }, 0);
  
  const coreCharges = validSelectedItems?.reduce((sum, item) => sum + (item?.coreCharge || 0) * (item?.quantity || 0), 0);
  
  // Calculate Bundle Savings (Difference between Original and Cart Price)
  const bundleSavings = validSelectedItems?.reduce((sum, item) => {
      if (item.bundleId && item.price > item.cartPrice) {
          return sum + (item.price - item.cartPrice) * item.quantity;
      }
      return sum;
  }, 0);

  // Tax Calculation Effect
  useEffect(() => {
    const calculateTax = async () => {
      // Use validSelectedItems instead of selectedItems which is undefined here
      if (!userAddress || !validSelectedItems || validSelectedItems.length === 0) {
        setEstimatedTax(0);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('calculate_cart_tax', {
          p_items: validSelectedItems.map(item => ({
            seller_id: item.sellerId, // camelCase as expected by our updated RPC logic or raw column
            price: item.cartPrice,
            quantity: item.quantity
          })),
          p_shipping_state_code: userAddress.state,
          p_shipping_city: userAddress.city,
          p_shipping_county: userAddress.county || ''
        });

        if (!error) {
          setEstimatedTax(data || 0);
        }
      } catch (err) {
        console.error('Tax calc error:', err);
        setEstimatedTax(0);
      }
    };
    calculateTax();
  }, [cartItems, userAddress]); // Updated dependencies

  // Total (Bundle savings already in subtotal due to using cartPrice)
  const total = subtotal + coreCharges + estimatedTax - discount;

  // ... (render logic unchanged)

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          {/* Symmetrical Friendly Availability Alert Modal */}
          {error && (
            <div className="fixed inset-0 bg-black/65 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white border border-slate-100 rounded-3xl max-w-sm w-full p-6 text-center shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl shadow-inner animate-bounce">
                  📦
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Item Temporarily Unavailable</h3>
                <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                  {error}
                </p>
                <Button
                  variant="default"
                  size="lg"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl py-3 font-bold transition shadow-lg shadow-slate-900/10 cursor-pointer"
                  onClick={() => setError(null)}
                >
                  Understand, Thank You!
                </Button>
              </div>
            </div>
          )}

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Shopping Cart</h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                {cartItems?.length} item{cartItems?.length !== 1 ? 's' : ''} in your cart
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <Link to="/model-catalog" className="w-full sm:w-auto">
                <Button variant="outline" iconName="Plus" iconPosition="left" className="w-full sm:w-auto">
                  Add More Items
                </Button>
              </Link>
              
              <Button
                variant="ghost"
                onClick={() => setShowCompatibilityWarnings(!showCompatibilityWarnings)}
                iconName={showCompatibilityWarnings ? "EyeOff" : "Eye"}
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                {showCompatibilityWarnings ? 'Hide' : 'Show'} Warnings
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Main Cart Content */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Select All & Bulk Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-card border border-border rounded-lg gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                  <span className="text-sm font-medium text-foreground">
                    Select all items ({cartItems?.length})
                  </span>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="ghost" size="sm" iconName="Heart" iconPosition="left" className="flex-1 sm:flex-none">
                    Save Selected
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    iconName="Trash2" 
                    iconPosition="left" 
                    className="flex-1 sm:flex-none"
                    onClick={() => {
                        const selectedIds = cartItems.filter(item => item.selected).map(item => item.cartItemId);
                        if (selectedIds.length > 0) {
                            if (confirm(`Remove ${selectedIds.length} items from cart?`)) {
                                Promise.all(selectedIds.map(id => handleRemoveItem(id)))
                                    .then(() => setCartItems(prev => prev.filter(i => !selectedIds.includes(i.cartItemId))))
                                    .catch(err => setError('Failed to remove some items.'));
                            }
                        }
                    }}
                  >
                    Remove Selected
                  </Button>
                </div>
              </div>

              {/* Compatibility Warnings */}
              {showCompatibilityWarnings && mockCompatibilityWarnings?.length > 0 && cartItems?.length > 1 && (
                <div>
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0" />
                    <h3 className="text-base sm:text-lg font-semibold text-foreground">
                      Compatibility Check
                    </h3>
                  </div>
                  <CompatibilityWarnings
                    warnings={mockCompatibilityWarnings}
                    onResolve={handleResolveWarning}
                    onIgnore={handleIgnoreWarning}
                  />
                </div>
              )}

              {/* Cart Items */}
              <div className="space-y-3 sm:space-y-4">
                {cartItems?.map((item) => (
                  <React.Fragment key={item?.cartItemId}>
                    <CartItem
                      item={item}
                      onUpdateQuantity={(id, qty) => handleUpdateQuantity(item?.cartItemId, qty)}
                      onRemove={() => handleRemoveItem(item?.cartItemId)}
                      onMoveToWishlist={() => handleMoveToWishlist(item?.cartItemId)}
                      onToggleSelect={handleToggleSelect}
                    />
                    
                    {/* NEW: Display Core Charge as a separate line item if applicable */}
                    {item.coreCharge > 0 && (
                      <div className="ml-8 sm:ml-12 p-3 bg-amber-50/50 border border-amber-100 rounded-lg flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded-full text-amber-600">
                            <Icon name="RefreshCw" size={16} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-amber-900">Core Charge</p>
                            <p className="text-[10px] text-amber-700 font-medium">Refundable deposit for {item.name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-amber-900">{formatPrice(item.coreCharge * item.quantity)}</p>
                          <p className="text-[10px] text-amber-600">{formatPrice(item.coreCharge)} × {item.quantity}</p>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Bundle Suggestions */}
              <BundleSuggestions
                suggestions={bundles}
                onAddBundle={handleAddBundle}
                onAddItem={handleAddItem}
              />
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <OrderSummary
                items={cartItems}
                itemCount={itemCount}
                subtotal={subtotal}
                coreCharges={coreCharges}
                discount={discount}
                bundleSavings={bundleSavings} // Pass bundle savings
                appliedCoupon={appliedCoupon}
                tax={estimatedTax}
                total={total}
                onApplyCoupon={handleApplyCoupon}
                onRemoveCoupon={handleRemoveCoupon}
                onProceedToCheckout={handleProceedToCheckout}
              />
            </div>
          </div>

          {/* Recommended Items - Full Width Section */}
          <RecommendedItems
            cartItems={cartItems}
            onAddToCart={handleAddFeaturedToCart}
            refreshTrigger={refreshFavorites}
          />

          {/* Saved Favorites - Full Width Section */}
          <div className="mt-8 sm:mt-12">
            <FeaturedModels
              onAddToCart={handleAddFeaturedToCart}
              refreshTrigger={refreshFavorites}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}